import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  #animationTimeout;
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;
  }

  animateSuccessfulAdd() {
    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !this.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement('fly-to-cart')
    );

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {AddToCartComponent | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer
 * @property {HTMLElement} liveRegion
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  static pendingProducts = new Set();

  #abortController = new AbortController();
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();

    if (this.#timeout) clearTimeout(this.#timeout);
  }

  #showError(message) {
    const error = this.refs.addToCartTextError;

    if (error) {
      let text = error.querySelector('[data-cart-error-message]');

      if (!text) {
        for (const node of [...error.childNodes]) {
          if (node.nodeType === 3) node.remove();
        }

        text = document.createElement('span');
        text.setAttribute('data-cart-error-message', '');
        error.appendChild(text);
      }

      text.textContent = message;
      error.classList.remove('hidden');
    }

    this.#setLiveRegionText(message);
  }

  async handleSubmit(event) {
    event.preventDefault();

    const form = this.querySelector('form');
    const button =
      this.refs.addToCartButtonContainer?.refs.addToCartButton;

    if (!form || button?.disabled || !form.checkValidity()) return;

    const limited = this.dataset.limitOne === 'true';
    const productId = String(this.dataset.productId || '');
    const key = productId || form.id;

    if (ProductFormComponent.pendingProducts.has(key)) return;

    if (this.#timeout) clearTimeout(this.#timeout);

    this.refs.addToCartTextError?.classList.add('hidden');
    this.#clearLiveRegionText();

    const formData = new FormData(form);
    const variantId = String(formData.get('id') || '');

    if (!variantId || (limited && !productId)) {
      this.#showError('Please select a flavor and try again.');
      return;
    }

    if (limited) formData.set('quantity', '1');

    ProductFormComponent.pendingProducts.add(key);

    try {
      if (limited) {
        let cart;

        try {
          const addUrl = new URL(
            Theme.routes.cart_add_url,
            window.location.origin
          );

          const cartPath = addUrl.pathname.replace(
            /\/cart\/add(?:\.js)?\/?$/,
            '/cart.js'
          );

          const root = window.Shopify?.routes?.root || '/';

          const cartUrl =
            cartPath !== addUrl.pathname
              ? cartPath
              : `${root}cart.js`;

          const check = await fetch(cartUrl, {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {
              Accept: 'application/json',
            },
          });

          if (!check.ok) {
            throw new Error(`Cart check HTTP ${check.status}`);
          }

          cart = await check.json();

          if (!Array.isArray(cart.items)) {
            throw new Error('Cart response has no items array');
          }
        } catch (error) {
          console.error('[Pizza limit: cart check]', error);
          this.#showError('Something went wrong. Please try again.');
          return;
        }

 const pizzaItems = cart.items.filter(
  (item) =>
    String(item.product_id) === productId &&
    item.quantity > 0
);

if (pizzaItems.length > 0) {
  const sameFlavor = pizzaItems.some(
    (item) => String(item.variant_id) === variantId
  );

const message = sameFlavor
  ? 'This flavor is already in your cart. Only one Candy Corn Pizza kit is allowed per order.'
  : 'Only one Candy Corn Pizza kit is allowed per order. To change flavors, remove the kit currently in your cart.';

  this.#showError(message);
  return;
}
      }

      if (
        !this.isConnected ||
        String(this.dataset.productId || '') !== productId ||
        String(this.refs.variantId.value) !== variantId ||
        this.refs.addToCartButtonContainer?.refs.addToCartButton?.disabled
      ) {
        return;
      }

      const sectionIds = [
        ...document.querySelectorAll('cart-items-component'),
      ]
        .map((item) => item.dataset.sectionId)
        .filter(Boolean);

      if (sectionIds.length) {
        formData.set(
          'sections',
          [...new Set(sectionIds)].join(',')
        );
      }

      const config = fetchConfig('javascript', {
        body: formData,
      });

      let result;
      let ok;

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          ...config,
          headers: {
            ...config.headers,
            Accept: 'application/json',
          },
        });

        ok = response.ok;
        result = await response.json();
      } catch (error) {
        console.error('[Pizza limit: add request]', error);

        this.#showError(
          'Something went wrong. Please refresh your cart before trying again.'
        );
        return;
      }

      const count =
        Number(formData.get('quantity')) ||
        Number(this.dataset.quantityDefault) ||
        1;

      if (!ok || result.status) {
        const message =
          result.description ||
          result.message ||
          'This item could not be added.';

        this.#showError(message);

        this.dispatchEvent(
          new CartErrorEvent(
            form.id || '',
            result.message,
            result.description,
            result.errors
          )
        );

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: count,
            productId,
          })
        );

        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'product-form-component',
          itemCount: count,
          productId,
          sections: result.sections,
        })
      );

      this.refs.addToCartTextError?.classList.add('hidden');

      this.#setLiveRegionText(
        Theme.translations.added || 'Added to cart'
      );

      this.#timeout = setTimeout(
        () => this.#clearLiveRegionText(),
        5000
      );

      try {
        this.refs.addToCartButtonContainer?.animateSuccessfulAdd?.();
      } catch (error) {
        console.error('[Pizza limit: animation]', error);
      }
    } catch (error) {
      console.error('[Pizza limit: theme UI]', error);

      this.#showError(
        'Please refresh your cart to confirm your selection.'
      );
    } finally {
      ProductFormComponent.pendingProducts.delete(key);

      cartPerformance.measureFromEvent(
        'add:user-action',
        event
      );
    }
  }

  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent = '';
  }

  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId =
        event.detail.data.newProduct.id;
    } else if (
      String(event.detail.data.productId) !==
      String(this.dataset.productId)
    ) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    variantId.value = event.detail.resource?.id ?? '';

    const current =
      addToCartButtonContainer?.refs.addToCartButton;

    const replacement =
      event.detail.data.html.querySelector(
        '[ref="addToCartButton"]'
      );

    if (!current) return;

    if (replacement) morph(current, replacement);

    if (!event.detail.resource?.available) {
      addToCartButtonContainer.disable();

      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        'hidden',
        'true'
      );
    } else {
      addToCartButtonContainer.enable();

      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute(
        'hidden'
      );
    }

    const image =
      event.detail.resource?.featured_media?.preview_image?.src;

    if (image) {
      addToCartButtonContainer?.setAttribute(
        'data-product-variant-media',
        image + '&width=100'
      );
    }

    this.refs.addToCartTextError?.classList.add('hidden');
    this.#clearLiveRegionText();
  };

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  source;
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = {
      x: startPoint.x,
      y: startPoint.y - 200,
    };

    const controlPoint2 = {
      x: endPoint.x - 300,
      y: endPoint.y - 100,
    };

    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint
      );

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    requestAnimationFrame(animate);
  }
}

function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x =
    aX * Math.pow(t, 3) +
    bX * Math.pow(t, 2) +
    cX * t +
    p0.x;

  const y =
    aY * Math.pow(t, 3) +
    bY * Math.pow(t, 2) +
    cY * t +
    p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}